import { subYears } from '@if/core/date'
import { getEGenderList, getELanguageList, getEMaritalStatusList, getERelationToApplicantList } from '@if/core/types'
import { ControlledStringDateOnlyPicker } from '@if/mui'
import { Divider, Grid, Typography } from '@mui/material'
import { InputTextField, InputTextFieldWithMask, SelectComponent } from '@src/components'
import { Applicant, CreditApplication, EMask } from '@src/data/types'
import { useMemo } from 'react'
import {
  Control,
  Controller,
  FieldError,
  FieldErrors,
  Merge,
  UseFormGetValues,
  UseFormRegister,
  UseFormTrigger,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'

interface Props {
  register: UseFormRegister<CreditApplication>
  getValues: UseFormGetValues<CreditApplication>
  trigger: UseFormTrigger<CreditApplication>
  errors: Merge<FieldError, FieldErrors<Applicant>> | undefined
  name: 'coApplicant' | 'applicant'
  formControl: Control<CreditApplication>
  editDisabled: boolean
}

const ApplicantInformationParameters = ({
  register,
  getValues,
  trigger,
  errors,
  name,
  formControl,
  editDisabled,
}: Props) => {
  const { t } = useTranslation()
  const genderOptions = useMemo(
    () => getEGenderList().map((item) => ({ label: t(`EGender.${item}`, { ns: 'enum' }), value: item })),
    [t],
  )
  const maritalStatusOptions = useMemo(
    () => getEMaritalStatusList().map((item) => ({ label: t(`EMaritalStatus.${item}`, { ns: 'enum' }), value: item })),
    [t],
  )
  const languageOptions = useMemo(
    () => getELanguageList().map((item) => ({ label: t(`ELanguage.${item}`, { ns: 'enum' }), value: item })),
    [t],
  )
  const relationWithApplicantOptions = useMemo(
    () =>
      getERelationToApplicantList().map((item) => ({
        label: t(`ERelationToApplicant.${item}`, { ns: 'enum' }),
        value: item,
      })),
    [t],
  )

  const handlePhoneOnchange = async () => {
    if (getValues('coApplicant')) {
      await Promise.all([trigger('applicant.cellPhone'), trigger('coApplicant.cellPhone')])
    }
  }

  return (
    <>
      <input type="hidden" {...register(`${name}.id`)} />
      <Divider style={{ fontSize: 25 }}>
        <Typography variant="h4" component="span" gutterBottom>
          {t('editCreditApplication.information.label')}
        </Typography>
      </Divider>
      <Grid container spacing={2} size={12}>
        <Grid
          size={{
            xs: 12,
            md: 3,
          }}
        >
          <InputTextField
            error={errors?.firstName}
            disabled={editDisabled}
            label={t('editCreditApplication.information.firstName')}
            {...register(`${name}.firstName`)}
            maxLength={50}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 3,
          }}
        >
          <InputTextField
            error={errors?.middleName}
            disabled={editDisabled}
            label={t('editCreditApplication.information.middleName')}
            {...register(`${name}.middleName`)}
            maxLength={50}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 3,
          }}
        >
          <InputTextField
            error={errors?.lastName}
            disabled={editDisabled}
            label={t('editCreditApplication.information.lastName')}
            {...register(`${name}.lastName`)}
            maxLength={50}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 3,
          }}
        >
          <ControlledStringDateOnlyPicker name={`${name}.birthDate`}
            control={formControl}
            label={t('editCreditApplication.information.birthDate')}
            maxDate={subYears(new Date(), 20)}
            minDate={subYears(new Date(), 100)}
            disabled={editDisabled}
            fullWidth
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 3,
          }}
        >
          <Controller
            name={`${name}.sin`}
            control={formControl}
            render={({ field }) => (
              <InputTextFieldWithMask
                mask={EMask.sinMask}
                error={errors?.sin}
                disabled={editDisabled}
                label={t('editCreditApplication.information.sin')}
                {...field}
              />
            )}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 3,
          }}
        >
          <SelectComponent
            items={genderOptions}
            label={t('editCreditApplication.information.genderId')}
            {...register(`${name}.genderId`)}
            error={errors?.genderId}
            disabled={editDisabled}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 3,
          }}
        >
          <SelectComponent
            items={maritalStatusOptions}
            label={t('editCreditApplication.information.maritalStatusId')}
            {...register(`${name}.maritalStatusId`)}
            error={errors?.maritalStatusId}
            disabled={editDisabled}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 3,
          }}
        >
          <SelectComponent
            items={languageOptions}
            label={t('editCreditApplication.information.languageId')}
            {...register(`${name}.languageId`)}
            error={errors?.languageId}
            disabled={editDisabled}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 3,
          }}
        >
          <Controller
            name={`${name}.homePhone`}
            control={formControl}
            render={({ field }) => (
              <InputTextFieldWithMask
                mask={EMask.phoneNumberMask}
                error={errors?.homePhone}
                label={t('editCreditApplication.information.homePhone')}
                {...field}
                disabled={editDisabled}
                onChange={(e) => field.onChange(e.target.value.substring(1).replace(/-/g, ''))}
              />
            )}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 3,
          }}
        >
          <Controller
            name={`${name}.cellPhone`}
            control={formControl}
            render={({ field }) => (
              <InputTextFieldWithMask
                mask={EMask.phoneNumberMask}
                error={errors?.cellPhone}
                label={t('editCreditApplication.information.cellPhone')}
                onKeyDown={handlePhoneOnchange}
                {...field}
                disabled={editDisabled}
                onChange={(e) => field.onChange(e.target.value.substring(1).replace(/-/g, ''))}
              />
            )}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 3,
          }}
        >
          <InputTextField
            error={errors?.email}
            label={t('editCreditApplication.information.email')}
            {...register(`${name}.email`)}
            maxLength={60}
            disabled={editDisabled}
          />
        </Grid>
        {name !== 'applicant' && (
          <Grid
            size={{
              xs: 12,
              md: 3,
            }}
          >
            <SelectComponent
              items={relationWithApplicantOptions}
              label={t('editCreditApplication.information.relationWithApplicant')}
              {...register(`${name}.relationWithApplicant`)}
              error={errors?.relationWithApplicant}
              disabled={editDisabled}
            />
          </Grid>
        )}
      </Grid>
    </>
  )
}
export default ApplicantInformationParameters
